"use client";

import * as React from "react";

import {
  ScrollArea,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
} from "@focal/ui-kit";
import { routes } from "@/constants";
import { useSessionContext } from "@/context";
import {
  NavAddMeeting,
  NavHeader,
  NavMain,
  NavUser,
} from "@/components/common/AppSidebar/components";
import { useIsMobileRedirect, useIsShortViewport } from "@/hooks";
import { useDeliverablesEnabled } from "@/hooks/useDeliverablesEnabled";
import { usePageStore } from "@/store/page";
import { FeatureFlagName } from "@/types/feature-flag";
import { NavItem } from "@/components/common";
import { useMemo } from "react";
import { Role } from "@/types";

let pylonChatIsOpen = false;
let pylonListenersAttached = false;

const ensurePylonListeners = () => {
  if (pylonListenersAttached || !window.Pylon) return;
  window.Pylon("onShow", () => {
    pylonChatIsOpen = true;
  });
  window.Pylon("onHide", () => {
    pylonChatIsOpen = false;
  });
  pylonListenersAttached = true;
};

const togglePylonSupportChat = (attempt = 0) => {
  if (window.Pylon) {
    ensurePylonListeners();
    window.Pylon(pylonChatIsOpen ? "hide" : "show");
    return;
  }

  if (attempt < 10) {
    window.setTimeout(() => togglePylonSupportChat(attempt + 1), 300);
  }
};

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { setHomeScroll, homeScroll } = usePageStore();
  const { user, session, getFeatureFlag } = useSessionContext();
  const isDeliverablesEnabled = useDeliverablesEnabled();
  const navMain: (NavItem | null)[] = [
    {
      title: "Today",
      icon: "EventIcon",
      url: routes.home.index,
      onClick: () => {
        setHomeScroll(homeScroll + 1);
      },
      dataAttr: "app_nav::today_nav_click",
      dataText: "Navigate to Today",
    },
    {
      title: "Conversations",
      icon: "ForumIcon",
      url: routes.conversations.index,
      dataAttr: "app_nav::conversations_nav_click",
      dataText: "Navigate to Conversations",
    },
    {
      title: "Meeting prep",
      icon: "MeetingPrepNotes",
      url: routes.meetingPrep.index,
      dataAttr: "app_nav::meeting_prep_nav_click",
      dataText: "Navigate to Meeting prep",
    },
    {
      title: "Clients",
      icon: "GroupIcon",
      url: routes.clients.index,
      dataAttr: "app_nav::clients_nav_click",
      dataText: "Navigate to Clients",
    },
    {
      title: "Insights",
      icon: "InsightsIcon",
      url: routes.insights.index,
      dataAttr: "app_nav::insights_nav_click",
      dataText: "Navigate to Insights",
    },
    ...(getFeatureFlag(FeatureFlagName.PDF_FILL_ENABLED).value === "on"
      ? [
          {
            title: "Form fill",
            icon: "DescriptionIcon" as const,
            url: routes.formFill.index,
          },
        ]
      : []),
    ...(isDeliverablesEnabled
      ? [
          {
            title: "Deliverables",
            icon: "PresentationIcon" as const,
            url: routes.deliverables.index,
            dataAttr: "app_nav::deliverables_nav_click",
            dataText: "Navigate to Deliverables",
          },
        ]
      : []),
  ];

  useIsMobileRedirect();
  const isShortViewport = useIsShortViewport();

  const userData = {
    name: user?.firstname ? `${user?.firstname} ${user?.lastname}` : "",
    email: user?.email || "",
    avatar: "/avatars/shadcn.jpg",
  };

  const isFirmAdmin = useMemo(() => {
    return (session?.roles || [])
      .map((role) => role.role_id)
      .includes(Role.FIRMADMIN);
  }, [session?.roles]);

  const navFooter: NavItem[] = [
    {
      title: "Getting started",
      icon: "HelpIcon",
      url: `https://support.meetwithfocal.com/`,
      dataAttr: "app_nav::getting_started_nav_click",
      dataText: "Navigate to Getting started",
    },
    {
      title: "Support chat",
      icon: "RateReviewIcon" as const,
      onClick: () => {
        togglePylonSupportChat();
      },
      dataAttr: "app_nav::support_chat_nav_click",
      dataText: "Open Support chat",
    },
    {
      title: "User integrations",
      icon: "IntegrationIcon",
      url: routes.settings.integrations,
      dataAttr: "app_nav::integrations_nav_click",
      dataText: "Navigate to User integrations",
    },
    {
      title: "Settings",
      icon: "SettingsIcon",
      url: routes.settings.account,
      dataAttr: "app_nav::settings_nav_click",
      dataText: "Navigate to Settings",
    },
  ];

  const navAdmin: NavItem[] = [
    {
      title: "Team management",
      icon: "UsersIcon",
      url: routes.admin.users,
      dataAttr: "app_nav::user_management_nav_click",
      dataText: "Navigate to Team management",
    },
    {
      title: "Firm settings",
      icon: "SettingsIcon",
      url: routes.admin.settings,
      dataAttr: "app_nav::firm_settings_nav_click",
      dataText: "Navigate to Firm settings",
    },
  ];

  const navMainWithNulls = navMain.filter((item) => item !== null);

  return (
    <Sidebar collapsible="icon" className="bg-background-tone" {...props}>
      <SidebarHeader className="pt-4">
        <NavHeader />
      </SidebarHeader>
      <SidebarContent className="overflow-hidden">
        <ScrollArea type="auto" className="flex-1 min-h-0">
          <div className="flex min-h-full flex-col">
            <NavAddMeeting />
            <NavMain items={navMainWithNulls} />
            <SidebarFooter className="mt-auto">
              <NavUser
                user={userData}
                items={navFooter}
                adminItems={isFirmAdmin ? navAdmin : undefined}
                compact={isShortViewport}
                pinnedItemUrl={routes.settings.account}
              />
            </SidebarFooter>
          </div>
        </ScrollArea>
      </SidebarContent>
      <SidebarRail />
    </Sidebar>
  );
}
